import React, { useState, useEffect, useMemo } from "react";
import { format, addDays, startOfWeek } from "date-fns";
import {
  getStaffFreeSlots,
  getStaffFreeSlotById,
  createAppointment,
  getAppointments,
  getAppointmentById,
  cancelAppointment,
} from "../../api/video-therapy/videoTherapy";
import { localToUTC, utcToLocal } from "../../hooks/useTimezoneConvert";
import toast from "react-hot-toast";
import { Paper, Box } from "@mui/material";
import AppointmentDialog from "../../component/therapy-sections/AppointmentDialog";
import AppointmentInfoDialog from "../../component/therapy-sections/AppointmentInfoDialog";
import WeekView from "../../component/therapy-sections/WeekView";
import WeekNavigation from "../../component/therapy-sections/WeekNavigation";
import MonthView from "../../component/therapy-sections/MonthView";
import { useTranslation } from "react-i18next";
import DayView from "../../component/therapy-sections/DayView";
import theme from "../../theme";

// Constants
const times = [
  "08:00",
  "08:30",
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
  "18:00",
  "18:30",
  "19:00",
  "19:30",
  "20:00",
];

interface AvailabilitySlot {
  id: string;
  staffId: string;
  clinicId: string;
  startTime: string;
  endTime: string;
  status: "available" | "booked" | "canceled";
  createdAt: string;
  updatedAt: string;
}

const generateWeekDays = (startDate: Date) =>
  Array.from({ length: 7 }, (_, i) => {
    const day = addDays(startDate, i);
    return {
      date: day,
      dayName: format(day, "EEE"),
      fullDate: format(day, "yyyy-MM-dd"),
      displayDate: format(day, "MMM d"),
    };
  });

// Main Page Component
const VideoTherapyPage: React.FC = () => {
  const { t } = useTranslation();
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const [isMonthView, setIsMonthView] = useState(false);
  const [forceSelectToday, setForceSelectToday] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [loadingAppointmentId, setLoadingAppointmentId] = useState<
    string | null
  >(null); // loading for appointment detail
  const [showForm, setShowForm] = useState(false);
  const [availabilitySlots, setAvailabilitySlots] = useState<
    AvailabilitySlot[]
  >([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    title: "Individual Therapy",
    description: "",
    start: "",
    end: "",
    selectedDate: "",
  });
  const [currentWeekStart, setCurrentWeekStart] = useState(weekStart);
  const [weekDays, setWeekDays] = useState(generateWeekDays(weekStart));
  const [_activeDay, setActiveDay] = useState(format(today, "EEE"));
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(
    null
  );
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<any | null>(
    null
  );
  const [loadingCancelId, setLoadingCancelId] = useState<string | null>(null);

  // Fetch slots
  const fetchSlots = async () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let startDateLocal, endDateLocal;
      if (isMonthView) {
        const monthStart = new Date(
          currentWeekStart.getFullYear(),
          currentWeekStart.getMonth(),
          1
        );
        const monthEnd = new Date(
          currentWeekStart.getFullYear(),
          currentWeekStart.getMonth() + 1,
          0
        );
        startDateLocal = format(monthStart, "yyyy-MM-dd") + "T00:00:00";
        endDateLocal = format(monthEnd, "yyyy-MM-dd") + "T23:59:59";
      } else {
        startDateLocal = format(currentWeekStart, "yyyy-MM-dd") + "T00:00:00";
        endDateLocal =
          format(addDays(currentWeekStart, 6), "yyyy-MM-dd") + "T23:59:59";
      }
      const startDateUTC = format(localToUTC(startDateLocal, tz), "yyyy-MM-dd");
      const endDateUTC = format(localToUTC(endDateLocal, tz), "yyyy-MM-dd");
      const res = await getStaffFreeSlots({
        startDate: startDateUTC,
        endDate: endDateUTC,
      });
      const slots = (res.data || []).map((slot: AvailabilitySlot) => ({
        ...slot,
        startTime: format(
          utcToLocal(slot.startTime, tz),
          "yyyy-MM-dd'T'HH:mm:ss"
        ),
        endTime: format(utcToLocal(slot.endTime, tz), "yyyy-MM-dd'T'HH:mm:ss"),
      }));
      setAvailabilitySlots(slots);
    } catch (error) {
      setAvailabilitySlots([]);
    }
  };

  const fetchAppointments = async () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let startDateLocal, endDateLocal;
      if (isMonthView) {
        const monthStart = new Date(
          currentWeekStart.getFullYear(),
          currentWeekStart.getMonth(),
          1
        );
        const monthEnd = new Date(
          currentWeekStart.getFullYear(),
          currentWeekStart.getMonth() + 1,
          0
        );
        startDateLocal = format(monthStart, "yyyy-MM-dd") + "T00:00:00";
        endDateLocal = format(monthEnd, "yyyy-MM-dd") + "T23:59:59";
      } else {
        startDateLocal = format(currentWeekStart, "yyyy-MM-dd") + "T00:00:00";
        endDateLocal =
          format(addDays(currentWeekStart, 6), "yyyy-MM-dd") + "T23:59:59";
      }
      const startDateUTC = localToUTC(startDateLocal, tz).toISOString();
      const endDateUTC = localToUTC(endDateLocal, tz).toISOString();
      const res = await getAppointments({
        startDate: startDateUTC,
        endDate: endDateUTC,
      });
      const appointmentsData = Array.isArray(res.data.data)
        ? res.data.data
        : [];
      const localAppointments = appointmentsData.map((app: any) => {
        const localStart = utcToLocal(app.startTime, tz);
        const localEnd = utcToLocal(app.endTime, tz);
        return {
          ...app,
          startTime: format(localStart, "yyyy-MM-dd'T'HH:mm:ss"),
          endTime: format(localEnd, "yyyy-MM-dd'T'HH:mm:ss"),
        };
      });
      setAppointments(localAppointments);
    } catch (error) {
      setAppointments([]);
    }
  };

  useEffect(() => {
    setWeekDays(generateWeekDays(currentWeekStart));
  }, [currentWeekStart]);

  useEffect(() => {
    fetchAppointments();
    fetchSlots();
  }, [currentWeekStart, isMonthView]);

  useEffect(() => {
    const intervalId = setInterval(
      () => {
        fetchAppointments();
        fetchSlots();
      },
      1 * 60 * 1000
    );
    return () => clearInterval(intervalId);
  }, [isMonthView]);

  useEffect(() => {
    // Reset forceSelectToday after it has been used
    if (forceSelectToday) {
      setForceSelectToday(false);
    }
  }, [forceSelectToday]);

  // Cell click logic
  const handleCellClick = (date: Date, time: string) => {
    const slotDateTime = new Date(`${format(date, "yyyy-MM-dd")}T${time}`);
    if (slotDateTime < new Date()) return;
    const dayStr = format(date, "EEE");
    const dateStr = format(date, "yyyy-MM-dd");
    const dateTimeStr = `${dateStr}T${time}`;
    const hasAppointment = appointments.find(
      (app) => app.day === dayStr && app.start === time
    );
    const hasAvailabilitySlot = availabilitySlots.find((slot) => {
      const slotStartDate = new Date(slot.startTime);
      const slotDate = format(slotStartDate, "yyyy-MM-dd");
      const slotTime = format(slotStartDate, "HH:mm");
      return slotDate === dateStr && slotTime === time;
    });
    if (hasAppointment) return;
    if (hasAvailabilitySlot) {
      setShowForm(true);
      setFormData({
        ...formData,
        start: dateTimeStr,
        end: dateTimeStr,
        selectedDate: dateStr,
      });
    }
  };

  // Slot click logic
  const handleSlotClick = async (slotId: string) => {
    const slot = availabilitySlots.find((s) => s.id === slotId);
    if (!slot) return;
    const isPastSlot = new Date(slot.startTime) < new Date();
    if (isPastSlot) return;
    // setLoadingSlotId(slotId);
    try {
      const res = await getStaffFreeSlotById(slotId);
      const selected = res.data;
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const localStart = utcToLocal(selected.startTime, tz);
      const localEnd = utcToLocal(selected.endTime, tz);
      const startDateStr = format(localStart, "yyyy-MM-dd");
      const startTimeStr = format(localStart, "HH:mm");
      const endDateStr = format(localEnd, "yyyy-MM-dd");
      const endTimeStr = format(localEnd, "HH:mm");
      setSelectedSlot(selected);
      setFormData({
        title: "Individual Therapy",
        description: "",
        selectedDate: startDateStr,
        start: `${startDateStr}T${startTimeStr}`,
        end: `${endDateStr}T${endTimeStr}`,
      });
      setShowForm(true);
    } catch (error) {
      setSelectedSlot(null);
    }
  };

  const handleAppointmentClick = async (appointment: any) => {
    setShowAppointmentDialog(true);
    setSelectedAppointment(null);
    setLoadingAppointmentId(appointment.id);

    try {
      const res = await getAppointmentById(appointment.appointmentId);
      setSelectedAppointment(res.data);
    } catch (error) {
      toast.error(t("title.fetchAppointmentFailed"));
    } finally {
      setLoadingAppointmentId(null);
    }
  };

  const handleCancelAppointment = async (appointmentId: string) => {
    setLoadingCancelId(appointmentId);
    try {
      await cancelAppointment(appointmentId);
      toast.success(t("appointment.cancelSuccess"));
      setShowAppointmentDialog(false);
      await fetchSlots();
      await fetchAppointments();
    } catch (error) {
      toast.error(t("appointment.cancelFailed"));
    } finally {
      setLoadingCancelId(null);
    }
  };

  const isCellClickable = (date: Date, time: string): boolean => {
    const dateStr = format(date, "yyyy-MM-dd");
    const slotDateTime = new Date(`${dateStr}T${time}`);

    const appointment = appointments.find((app) => {
      const appStart = new Date(app.startTime);
      const appEnd = new Date(app.endTime);
      return slotDateTime >= appStart && slotDateTime < appEnd;
    });
    if (appointment) {
      const now = new Date();
      const appStart = new Date(appointment.startTime);
      const appEnd = new Date(appointment.endTime);
      if (now >= appStart && now < appEnd) return true;
      if (slotDateTime >= now) return true;
      return false;
    }

    const hasAvailabilitySlot = availabilitySlots.find((slot) => {
      const slotStartDate = new Date(slot.startTime);
      const slotDate = format(slotStartDate, "yyyy-MM-dd");
      const slotTime = format(slotStartDate, "HH:mm");
      return slotDate === dateStr && slotTime === time;
    });
    return !!hasAvailabilitySlot && slotDateTime >= new Date();
  };

  // Submit appointment
  const submitAppointment = async (data: any) => {
    if (selectedSlot) {
      const payload = {
        slotId: selectedSlot.id,
        title: data.title,
        description: data.description,
      };
      try {
        await createAppointment(payload);
        setShowForm(false);
        toast.success(t("title.appointmentCreated"));
        await fetchAppointments();
      } catch (error) {
        toast.error(t("title.appointmentFailed"));
      }
    }
  };

  const statusMap = useMemo(() => {
    const map: Record<string, any> = {};
    const start = new Date(currentWeekStart);
    const end = new Date(currentWeekStart);
    end.setDate(end.getDate() + 7);
    for (let d = start; d <= end; d.setMinutes(d.getMinutes() + 30)) {
      const key = format(d, "yyyy-MM-dd HH:mm");
      const startTime = new Date(d);
      const endTime = new Date(d);
      endTime.setMinutes(endTime.getMinutes() + 30);
      map[key] = {
        startTime: startTime,
        endTime: endTime,
      };
    }

    // Mapping slot
    availabilitySlots.forEach((slot) => {
      const key = format(new Date(slot.startTime), "yyyy-MM-dd HH:mm");
      map[key] = {
        ...(map[key] || {}),
        slotId: slot.id,
        status: slot.status || "available",
      };
    });

    appointments.forEach((appointment) => {
      const key = format(new Date(appointment.startTime), "yyyy-MM-dd HH:mm");
      if (
        appointment.status === "canceled" ||
        appointment.status === "declined"
      ) {
        return;
      }
      map[key] = {
        ...(map[key] ?? {}),
        appointmentId: appointment.id,
        status: appointment.status || "pending",
        title: appointment.title,
      };
    });
    return map;
  }, [currentWeekStart, appointments, availabilitySlots]);

  return (
    <Box className="md:p-4 px-1 md:px-2 mt-1">
      <Paper
        elevation={3}
        className="w-full md:p-6 mb-16 md:mb-0 px-2 "
        sx={{
          borderRadius: 4,
          bgcolor: "background.paper",
          height: "84.5vh",
          overflow: "auto",
          [theme.breakpoints.down("md")]: { height: "78vh" },
          "@media (min-width:768px) and (max-width:1024px)": {
            height: "78vh",
          },
        }}
      >
        {/* <Box
          className="flex justify-between items-center mb-4"
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Typography
            variant="h5"
            className="font-semibold"
            sx={{ fontWeight: 600 }}
          >
            {t("title.videoTherapy")}
          </Typography>
        </Box> */}
        <Box sx={{ mb: { xs: 0, md: 3 } }}>
          <WeekNavigation
            today={today}
            currentWeekStart={currentWeekStart}
            setCurrentWeekStart={setCurrentWeekStart}
            setActiveDay={setActiveDay}
            weekDays={weekDays}
            isMonthView={isMonthView}
            setForceSelectToday={setForceSelectToday}
            setIsMonthView={(val) => {
              setIsMonthView(val);
              if (val) {
                const todayDate = new Date();
                const monthStart = new Date(
                  todayDate.getFullYear(),
                  todayDate.getMonth(),
                  1
                );
                setCurrentWeekStart(monthStart);
                setForceSelectToday(true);
                setSelectedDate(format(today, "yyyy-MM-dd"));
              } else {
                const weekStart = startOfWeek(today, { weekStartsOn: 1 });
                setCurrentWeekStart(weekStart);
                setActiveDay(format(today, "EEE"));
              }
            }}
          />
        </Box>
        {isMonthView ? (
          <MonthView
            currentMonthStart={currentWeekStart}
            today={today}
            appointments={appointments}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            forceSelectToday={forceSelectToday}
            viewMode={isMonthView ? "month" : "week"}
          />
        ) : (
          <>
            <WeekView
              weekDays={weekDays}
              times={times}
              statusMap={statusMap}
              handleCellClick={handleCellClick}
              handleSlotClick={handleSlotClick}
              isCellClickable={isCellClickable}
              setSelectedAppointment={handleAppointmentClick}
              setShowAppointmentDialog={setShowAppointmentDialog}
            />
            <DayView
              weekDays={weekDays}
              times={times}
              statusMap={statusMap}
              handleCellClick={handleCellClick}
              handleSlotClick={handleSlotClick}
              isCellClickable={isCellClickable}
              setSelectedAppointment={handleAppointmentClick}
              setShowAppointmentDialog={setShowAppointmentDialog}
              activeDay={_activeDay}
              setActiveDay={setActiveDay}
            />
          </>
        )}
      </Paper>
      <AppointmentDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        formData={formData}
        setFormData={setFormData}
        submitAppointment={submitAppointment}
      />

      <AppointmentInfoDialog
        open={showAppointmentDialog}
        onClose={() => setShowAppointmentDialog(false)}
        appointment={selectedAppointment}
        loading={
          !!loadingAppointmentId || loadingCancelId === selectedAppointment?.id
        }
        onCancelAppointment={handleCancelAppointment}
      />
    </Box>
  );
};

export default VideoTherapyPage;
